<template>
  <div class="policy-page">
    <main-header />
    
    <div class="container policy-container">
      <h1 class="page-title">Privacy Policy</h1>
      
      <div class="policy-content">
        <section>
          <h2>Protecting Your Privacy</h2>
          <p>My Oxytocin is committed to keeping your personal information safe. We want to earn your trust by telling you how we collect, use and safeguard information we collect about you. Please read this privacy policy carefully. If you do not agree with our practices, please do not register, subscribe, create an account, make purchases or otherwise interact with My Oxytocin at www.myoxytocin.com or any other website or online service that links to this policy (the "Site"), or otherwise provide personal information to My Oxytocin. By visiting and using the features on the Site or by otherwise interacting with My Oxytocin, you accept the practices, terms, and conditions described in this privacy policy. This policy does not govern the terms of our relationship or constitute any representation or warranty. The terms of our relationship are separately governed by our Terms and Conditions.</p>
          <p>My Oxytocin is the sole owner of the information collected on this Site. We make every effort to ensure that any data that we store about you is kept secure. This privacy policy discloses our practices, terms and conditions for the gathering and use of information from visitors to our Site or when you otherwise interact with us.</p>
        </section>

        <section>
          <h2>Jurisdiction</h2>
          <p>This privacy policy is directed at consumers in the United States. The laws of the United States shall apply to the collection of personal information and other information through the Site, and to My Oxytocin use of such information. If you are accessing this Site from outside of the U.S., please note that the laws of the United States may not provide a level of protection equivalent to the laws in your home country.</p>
        </section>

        <section>
          <h2>Security</h2>
          <p>We secure all of our online ordering information including your name, address, and credit card numbers (card numbers are encrypted) to protect your billing information as it travels through the purchase system. In addition, all credit card transactions are verified and processed by Stripe, a third-party financial institution that receives credit card numbers and other personal information in an encrypted format. We take appropriate security measures to protect user data offline as well. Although we use our best efforts to protect the security of your information and our system, no security measures can be guaranteed to be 100% secure. You assume that risk by using the Site.</p>
          <p>You do not need to set up a user id and password to use our Site because we do not require you to establish your identity when you visit our Site. However, if you are a customer seeking access to your account information, you will have to log in using a password. We recommend you do not divulge your password to anyone. My Oxytocin will never ask you for your password in an unsolicited telephone call or e-mail. You are responsible for the secrecy of your passwords.</p>
        </section>

        <section>
          <h2>Personal Information Collected by My Oxytocin</h2>
          <p>We may collect the following categories of personal information:</p>
          <ul>
            <li>Personal Identifiers</li>
            <li>Demographic Information</li>
            <li>Financial Information</li>
            <li>Purchase History Information</li>
            <li>Internet, Application, and Network Activity</li>
            <li>Device and Online Identifiers and Related Information</li>
          </ul>
          <p>We collect this personal information, which includes identifiers such as name and email address, mailing address, birthday and other information if, for example, you complete our order forms, surveys, or registration forms, subscribe to our services or newsletters, or participate in one of our contests or sweepstakes. If you choose to make a purchase, we will collect your payment information and information about your transaction with us, such as the items you purchased.</p>
          <p>If you send a gift from our Site or through other means, we will collect and use the information provided to us about you and the gift recipient in line with our stated policies for direct purchasers. Gift recipients can opt-out of receiving any emails or newsletters by contacting us at support@myoxytocin.com.</p>
          <p>We also automatically collect certain information when you visit our Site which helps us understand how visitors browse our Site, so that we can make their shopping experience better. This may include referring domains, the type of browsers used, operating system software, browser settings, cookie preferences and data associated with cookies and similar technologies, search engine keywords and JavaScript enablement. We may also record number of visits, paths taken, and time spent on sites and pages. In addition, we may collect Internet or other electronic network activity information, including, but not limited to, browsing history, search history, and information regarding your interaction with the website, any application we may offer or our advertising. We may draw inferences from the information we collect to develop a profile to help us better understand our audience and market to you. Please see the section below titled "Cookies and Other Automatically Collected Information" for more details. Our Site may have online chat and may have forums, message boards, and news groups. Any information you disclose in these areas becomes public information, and you should exercise caution when deciding to disclose your personal information to people you do not know. If you disclose your e-mail address in a chat room or forum, you may receive unsolicited e-mails or spam from third parties. We cannot safeguard the privacy of any such information you choose to disclose online in this manner and you must do so carefully and at your own risk.</p>
        </section>

        <section>
          <h2>Use of Personal Information</h2>
          <p>Personal information may be used in several ways:</p>
          <ul>
            <li>To enable us to respond to you or to process, validate, and verify service requests and to perform the requested services.</li>
            <li>To allow us to charge and invoice you and to contact you regarding changes to the subscribed services.</li>
            <li>To give our Site designers information that is useful for developing new features and services.</li>
            <li>To tailor the customer's experience on our Site, showing them content we think might interest them or altering the content according to their preferences.</li>
            <li>To serve advertisements tailored to your interests on the Site or third party websites.</li>
            <li>To keep friends and customers apprised of our release dates, purchasing opportunities, special events and offers.</li>
            <li>To protect us and our rights.</li>
          </ul>
        </section>

        <section>
          <h2>Disclosure of Personal Information</h2>
          <p>We do not provide or sell your information to other parties, except as otherwise disclosed in this privacy policy. We may share the personal information described above with third parties as follows:</p>
          <ul>
            <li>With third party service providers or others for business purposes, such as outside shipping companies, marketing service providers and credit card processing companies.</li>
            <li>In connection with a merger, acquisition, consolidation, change of control, or sale of all or a portion of our assets or if we undergo bankruptcy or liquidation.</li>
            <li>If we believe it is necessary in order to detect, investigate, prevent, or take action against illegal activities, fraud, or situations involving potential threats to the rights, property, or personal safety of any person.</li>
            <li>As required by law and/or to comply with a judicial proceeding, court order, or legal process served on our Site.</li>
            <li>To establish, protect, or exercise our legal rights, to defend against legal claims or demands, or to comply with the requirements of any mandatory applicable law.</li>
            <li>If you otherwise provide consent.</li>
          </ul>
          <p>My Oxytocin discloses the following categories of information for business purposes, such as when we disclose information to vendors who help us provide our products and services:</p>
          <p>My Oxytocin may share aggregate statistical information gathered from use of the Site with advertisers, business partners, sponsor or other third parties. For example, we may tell an advertiser that X number of visitors visited a certain area on a web site or that ABCdomain referred Y number of visitors to our website.</p>
        </section>

        <section>
          <h2>Retention of Personal Information</h2>
          <p>We will retain your information for as long as your account is active or as necessary to provide you services and to maintain a record of your transactions for financial reporting and compliance purposes. If at any time you would like to remove your name or update the information you have provided, you may go to the Edit Your Information section of the Site.</p>
        </section>

        <section>
          <h2>Links to Third-Party Websites</h2>
          <p>This Site may contain links to other sites. My Oxytocin is not responsible for the privacy practices of other sites. We encourage our users to be aware when they leave our Site and to read the privacy statements of each site that collects personal information. This privacy policy applies solely to information collected by this Site.</p>
        </section>

        <section>
          <h2>Cookies and Automatically Collected Information</h2>
          <p>Your browser offers cookies that store small amounts of data on your computer about your visits to our Site. Cookies assist us in tracking which of our features you like best. And when you re-visit our Site, cookies can enable us to recognize that you are a return visitor and to customize our content according to your preferences. Among other things, we may use cookies to:</p>
          <ul>
            <li>Keep count of your return visits to our Site</li>
            <li>Accumulate and report anonymous, aggregate, statistical information on Site usage</li>
            <li>Deliver content specific to your interests</li>
            <li>Save your password so you don't have to re-enter it each time you visit our Site</li>
          </ul>
          <p>In general, unless you are a registered user with an account with us, we do not merge or associate our cookie information with any other personal information obtained by us. However, we may share select personal and non-personal information with a third party advertising partner and its service providers, or they may collect such information directly on the Site in order to deliver tailored advertising to you that matches your interests when you visit our Site as well as other websites. Third party advertising partners may track your activity over time and across sites and devices.</p>
          <p>You have control over cookies. By modifying your browser preferences, you have the choice to accept all cookies, to be notified when a cookie is set, or to reject all cookies. If you reject some or all cookies, your experience at our Site and on other sites may not be complete. Some sites require cookies to function, such as those with certain e-commerce features. If you do not allow cookies, you will not be able to take advantage of personalized content delivery offered by us or other internet sites.</p>
          <p>By actively continuing to use this Site, you consent to the use of cookies and similar technology for the purposes of improving our Site and analyzing use of the Site, personalize the website content to you, deliver advertising messages and offers tailored to you and for marketing purposes. Among the tools we use for these purposes is Google Analytics. For more information about how Google Analytics interacts with information, please visit www.google.com/policies/privacy/partners/.</p>
          <p>The Site does not currently respond to "Do Not Track" signals sent by Internet browsers. If we do so in the future, we will describe how we do so in this Privacy Policy.</p>
        </section>

        <section>
          <h2>Your Privacy Choices</h2>
          <p>You may have certain rights relating to your personal information. If you would like to exercise your rights or make a request, please email us at support@myoxytocin.com. Those rights may include:</p>
          <ul>
            <li><strong>Right to access personal information.</strong> You may be entitled to receive the specific pieces of your personal information we hold.</li>
            <li><strong>Right to data portability.</strong> You may be entitled to receive a copy of your electronic personal information in a readily-usable format.</li>
            <li><strong>Right to disclosure.</strong> You may be entitled to receive information regarding the categories of personal information we collected, the sources from which we collected personal information, the purposes for which we collected and shared personal information, and the categories personal information that we disclosed for a business purpose in the 12 months preceding your request.</li>
            <li><strong>Right to deletion.</strong> You may be entitled to request that we delete the personal information that we have collected from you. We will use commercially reasonable efforts to honor your request, in compliance with applicable laws. Please note, however, that we may need or be required to keep such information, such as for our legitimate business purposes or to comply with applicable law.</li>
          </ul>
          <p>To protect your privacy and security, we take reasonable steps to verify your identity before acting on requests.</p>
        </section>

        <section>
          <h2>Children's Privacy</h2>
          <p>This Site is not directed to children and we do not intentionally collect or maintain any personal information from children. If you believe that we might have any personal information from or about anyone under the age of 21, please reach us at the contact information provided below.</p>
        </section>

        <section>
          <h2>Contact Us</h2>
          <p>If you have any questions about this privacy statement, our online practices, and/ or your dealings with our Site, you can contact us at <a href="mailto:support@myoxytocin.com">support@myoxytocin.com</a>. We reserve the right, in our sole discretion, to modify, alter or otherwise update this policy at any time and you agree to be bound by such modifications, alterations or updates. We will notify you of material changes to this privacy policy by posting the revised policy with the date it was revised on this page and if you have registered with us, we may notify you by email to your email address of record. Your continued use (including access, browsing and/or use of any interactive feature) of our Site constitutes your agreement to this privacy policy and any updates. We encourage you to periodically review this privacy policy to stay informed about how we are protecting the personal information we collect.</p>
        </section>
      </div>
    </div>
    
    <main-footer />
  </div>
</template>

<script>
import MainHeader from '@/components/MainHeader.vue';
import MainFooter from '@/components/MainFooter.vue';

export default {
  name: 'PrivacyPolicyView',
  components: {
    MainHeader,
    MainFooter
  }
};
</script>

<style scoped>
.policy-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.policy-container {
  flex: 1;
  padding: 6rem 2rem;
  max-width: 1000px;
  margin: 0 auto;
}

.page-title {
  font-size: 4rem;
  font-weight: 700;
  color: var(--text);
  text-align: center;
  margin-bottom: 5rem;
}

.policy-content {
  display: flex;
  flex-direction: column;
  gap: 4rem;
}

.policy-content section {
  background-color: white;
  padding: 3rem;
  border-radius: 12px;
  border: 1px solid var(--grey-2);
}

.policy-content h2 {
  font-size: 2.4rem;
  font-weight: 700;
  color: var(--dark-green);
  margin-bottom: 2rem;
}

.policy-content p {
  font-size: 1.6rem;
  line-height: 1.8;
  color: var(--text);
  margin-bottom: 1.5rem;
}

.policy-content p:last-child {
  margin-bottom: 0;
}

.policy-content ul {
  margin-left: 2rem;
  margin-bottom: 1.5rem;
}

.policy-content li {
  font-size: 1.6rem;
  line-height: 1.8;
  color: var(--text);
  margin-bottom: 1rem;
}

.policy-content a {
  color: var(--dark-green);
  text-decoration: underline;
}

.policy-content a:hover {
  color: var(--dark-blue);
}

@media (max-width: 768px) {
  .policy-container {
    padding: 4rem 1.5rem;
  }
  
  .page-title {
    font-size: 3rem;
    margin-bottom: 3rem;
  }
  
  .policy-content {
    gap: 3rem;
  }
  
  .policy-content section {
    padding: 2rem;
  }
  
  .policy-content h2 {
    font-size: 2rem;
  }
  
  .policy-content p,
  .policy-content li {
    font-size: 1.5rem;
  }
}
</style>
